import { useTranslation } from 'react-i18next';
import { useLanguageNavigation } from './LanguageRouter';
import { Button } from '@/components/ui/button';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover';
import { useState } from 'react';
import { cn } from '@/lib/utils';
import gb40 from '@/assets/flags/gb-40.png';
import gb80 from '@/assets/flags/gb-80.png';
import de40 from '@/assets/flags/de-40.png';
import de80 from '@/assets/flags/de-80.png';
import fr40 from '@/assets/flags/fr-40.png';
import fr80 from '@/assets/flags/fr-80.png';
import it40 from '@/assets/flags/it-40.png';
import it80 from '@/assets/flags/it-80.png';
import es40 from '@/assets/flags/es-40.png';
import es80 from '@/assets/flags/es-80.png';

// Self-hosted by CWS-139. These were fetched from a third-party flag CDN on
// every page view, sending each visitor's IP to its operator on a site that
// sells EU privacy. Imported rather than placed in public/ so Vite fingerprints
// them with the chunk that uses them; all ten are under Vite's 4096-byte
// assetsInlineLimit, so they ship inlined as data: URIs and cost no request at
// all. SYSTEM_DESIGN.md §22 records the measurement and why SVG was rejected.
//
// The CDN host is deliberately not named here: src/lib/thirdPartyOrigins.test.ts
// fails on that literal appearing anywhere under src/, and this file is the one
// it most needs to watch.
const FLAGS: Record<string, { x1: string; x2: string }> = {
  gb: { x1: gb40, x2: gb80 },
  de: { x1: de40, x2: de80 },
  fr: { x1: fr40, x2: fr80 },
  it: { x1: it40, x2: it80 },
  es: { x1: es40, x2: es80 },
};

const languages = [
  { code: 'en', name: 'English', countryCode: 'gb' },
  { code: 'de', name: 'Deutsch', countryCode: 'de' },
  { code: 'fr', name: 'Français', countryCode: 'fr' },
  { code: 'it', name: 'Italiano', countryCode: 'it' },
  { code: 'es', name: 'Español', countryCode: 'es' },
];

function Flag({ countryCode, className }: { countryCode: string; className?: string }) {
  const flag = FLAGS[countryCode];

  // A locale added to `languages` without a flag must not white-screen the
  // site. `Flag` renders inside `Header`, which `LanguageRouter` wraps around
  // every route; before CWS-5's error boundaries an unguarded `flag.x1` would
  // have blanked every page rather than one image.
  // Returning null degrades to the code beside it, which is what the previous
  // third-party `alt=""` image already did when the CDN was unreachable.
  // `languageListContract.test.ts` is what makes the gap loud instead.
  if (!flag) {
    return null;
  }

  return (
    <img
      src={flag.x1}
      srcSet={`${flag.x2} 2x`}
      alt=""
      className={cn("h-4 w-6 rounded-[2px] object-cover", className)}
      loading="lazy"
    />
  );
}

export function LanguageSwitcher() {
  const { i18n } = useTranslation();
  const { changeLanguage } = useLanguageNavigation();
  const [open, setOpen] = useState(false);

  const currentLang = languages.find(l => l.code === i18n.language?.split('-')[0]) || languages[0];

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button variant="ghost" size="sm" className="gap-2 text-muted-foreground hover:text-foreground">
          <Flag countryCode={currentLang.countryCode} />
          <span className="text-xs">{currentLang.code.toUpperCase()}</span>
        </Button>
      </PopoverTrigger>
      <PopoverContent align="end" className="w-auto p-2" sideOffset={8}>
        <div className="grid grid-cols-2 gap-1">
          {languages.map((lang) => (
            <button
              key={lang.code}
              onClick={() => {
                changeLanguage(lang.code);
                setOpen(false);
              }}
              className={cn(
                "flex items-center gap-2.5 rounded-md px-3 py-2 text-sm transition-colors hover:bg-accent",
                "text-left whitespace-nowrap",
                lang.code === currentLang.code && "bg-accent font-medium"
              )}
            >
              <Flag countryCode={lang.countryCode} />
              <span>{lang.name}</span>
            </button>
          ))}
        </div>
      </PopoverContent>
    </Popover>
  );
}
